/* ==========================================================================
   L’Adresse Parfumée — comportements communs
   Panier AJAX + tiroir, ajout rapide, en-tête, méga-menu, recherche,
   carrousels, apparitions. Aucune dépendance.
   ========================================================================== */
(() => {
  'use strict';

  const AP = (window.AP = window.AP || {});
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- argent */
  AP.money = (centimes) => {
    const n = Math.round(+centimes || 0);
    const e = Math.floor(n / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
    const c = n % 100;
    return `${e}${c ? `,${String(c).padStart(2, '0')}` : ''}\u00a0€`;
  };

  /* ---------------------------------------------------------------- toast */
  let toastTimer;
  AP.toast = (msg) => {
    let t = $('.ap-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'ap-toast';
      t.setAttribute('role', 'status');
      document.body.appendChild(t);
    }
    t.textContent = msg;
    requestAnimationFrame(() => t.classList.add('is-on'));
    setTimeout(() => t.classList.add('is-on'), 30);
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove('is-on'), 2600);
  };

  /* ---------------------------------------------------------------- verrou de défilement */
  const verrous = new Set();
  const verrouiller = (qui, oui) => {
    if (oui) verrous.add(qui);
    else verrous.delete(qui);
    document.body.classList.toggle('ap-lock', verrous.size > 0);
  };

  /* ---------------------------------------------------------------- piège à focus minimal */
  const focusables = (el) => $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', el).filter((x) => x.offsetParent !== null || x === document.activeElement);
  const pieger = (el, e) => {
    if (e.key !== 'Tab') return;
    const f = focusables(el);
    if (!f.length) return;
    const premier = f[0];
    const dernier = f[f.length - 1];
    if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus(); }
    else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus(); }
  };

  /* ==========================================================================
     Apparitions au défilement
     ========================================================================== */
  const observerApparitions = () => {
    const cibles = $$('[data-reveal]:not(.is-in)');
    if (reduit || !('IntersectionObserver' in window)) { cibles.forEach((c) => c.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entrees) => {
      entrees.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    cibles.forEach((c) => io.observe(c));
    // filet de sécurité : rien ne doit rester invisible
    setTimeout(() => cibles.forEach((c) => { const r = c.getBoundingClientRect(); if (r.top < window.innerHeight) c.classList.add('is-in'); }), 1200);
  };
  AP.reveal = observerApparitions;

  /* ==========================================================================
     Bandeau d’annonces (rotation sur mobile)
     ========================================================================== */
  const annonces = () => {
    $$('[data-announce]').forEach((b) => {
      const items = $$('.ap-announce__item', b);
      if (items.length < 2) return;
      let i = 0;
      const pas = (+b.dataset.speed || 4) * 1000;
      setInterval(() => {
        if (document.hidden) return;
        items[i].classList.remove('is-on');
        i = (i + 1) % items.length;
        items[i].classList.add('is-on');
      }, pas);
    });
  };

  /* ==========================================================================
     En-tête : hauteur, état défilé, méga-menu, menu mobile, recherche
     ========================================================================== */
  const entete = () => {
    const h = $('[data-header]');
    if (!h) return;
    const mesurer = () => document.documentElement.style.setProperty('--ap-header-h', `${Math.round(h.querySelector('.ap-header__bar').getBoundingClientRect().height)}px`);
    mesurer();
    window.addEventListener('resize', mesurer, { passive: true });

    // état « défilé » : une sentinelle en haut de page
    const sentinelle = document.createElement('div');
    sentinelle.style.cssText = 'position:absolute;top:0;left:0;height:24px;width:1px;pointer-events:none';
    document.body.prepend(sentinelle);
    new IntersectionObserver(([e]) => h.classList.toggle('is-scrolled', !e.isIntersecting)).observe(sentinelle);

    // méga-menu
    const mega = $('[data-mega]', h);
    const bouton = $('[data-mega-toggle]', h);
    if (mega && bouton) {
      let timer;
      const ouvrir = (oui) => {
        clearTimeout(timer);
        mega.classList.toggle('is-open', oui);
        h.classList.toggle('is-open', oui);
        bouton.setAttribute('aria-expanded', String(oui));
      };
      bouton.addEventListener('click', () => ouvrir(!mega.classList.contains('is-open')));
      const survol = window.matchMedia('(hover: hover)').matches;
      if (survol) {
        bouton.addEventListener('mouseenter', () => { clearTimeout(timer); timer = setTimeout(() => ouvrir(true), 90); });
        bouton.addEventListener('mouseleave', () => { clearTimeout(timer); timer = setTimeout(() => ouvrir(false), 220); });
        mega.addEventListener('mouseenter', () => clearTimeout(timer));
        mega.addEventListener('mouseleave', () => { timer = setTimeout(() => ouvrir(false), 220); });
      }
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && mega.classList.contains('is-open')) { ouvrir(false); bouton.focus(); } });
      document.addEventListener('click', (e) => { if (mega.classList.contains('is-open') && !h.contains(e.target)) ouvrir(false); });
    }

    // menu déroulant simple (liens avec sous-liens)
    $$('.ap-nav__drop', h).forEach((d) => {
      document.addEventListener('click', (e) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
    });

    // menu mobile
    const menu = $('[data-menu]');
    if (menu) {
      const btn = $('[data-menu-open]');
      const ouvrir = (oui) => {
        menu.classList.toggle('is-open', oui);
        menu.setAttribute('aria-hidden', String(!oui));
        btn?.setAttribute('aria-expanded', String(oui));
        verrouiller('menu', oui);
        if (oui) setTimeout(() => $('[data-menu-close]', menu)?.focus(), 60);
        else btn?.focus();
      };
      btn?.addEventListener('click', () => ouvrir(true));
      $('[data-menu-close]', menu)?.addEventListener('click', () => ouvrir(false));
      menu.addEventListener('keydown', (e) => { if (e.key === 'Escape') ouvrir(false); pieger(menu, e); });
      $$('a', menu).forEach((a) => a.addEventListener('click', () => { if (a.getAttribute('href')?.includes('#')) ouvrir(false); }));
    }

    recherche();
  };

  /* ---------------------------------------------------------------- recherche instantanée */
  const recherche = () => {
    const s = $('[data-search]');
    if (!s) return;
    const input = $('[data-search-input]', s);
    const sortie = $('[data-search-results]', s);
    const indice = $('[data-search-hint]', s);
    let dernier = null;
    let ctrl;
    const ouvrir = (oui) => {
      s.classList.toggle('is-open', oui);
      s.setAttribute('aria-hidden', String(!oui));
      verrouiller('search', oui);
      if (oui) setTimeout(() => input.focus(), 80);
    };
    $$('[data-search-open]').forEach((b) => b.addEventListener('click', () => ouvrir(true)));
    $('[data-search-close]', s)?.addEventListener('click', () => ouvrir(false));
    s.addEventListener('click', (e) => { if (e.target === s) ouvrir(false); });
    s.addEventListener('keydown', (e) => { if (e.key === 'Escape') ouvrir(false); pieger(s, e); });

    const chercher = async (q) => {
      q = q.trim();
      if (q === dernier) return;
      dernier = q;
      if (q.length < 2) { sortie.innerHTML = ''; indice.hidden = false; return; }
      indice.hidden = true;
      ctrl?.abort();
      ctrl = new AbortController();
      const url = `${AP.routes.predictive}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,tag,body,variants.title&section_id=ap-predictive-search`;
      try {
        const r = await fetch(url, { signal: ctrl.signal });
        if (!r.ok) throw new Error(r.status);
        const html = await r.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const racine = doc.querySelector('.shopify-section') || doc.body;
        sortie.innerHTML = racine.innerHTML;
      } catch (err) {
        if (err.name !== 'AbortError') sortie.innerHTML = '';
      }
    };
    let t;
    input.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => chercher(input.value), 180); });
    $$('[data-search-suggest]', s).forEach((b) => b.addEventListener('click', () => { input.value = b.dataset.searchSuggest; chercher(input.value); input.focus(); }));
  };

  /* ==========================================================================
     Panier
     ========================================================================== */
  const drawer = () => $('[data-cd]');
  const ouvrirPanier = (oui = true) => {
    const d = drawer();
    const o = $('[data-cd-overlay]');
    if (!d) return false;
    d.classList.toggle('is-open', oui);
    o?.classList.toggle('is-open', oui);
    d.setAttribute('aria-hidden', String(!oui));
    verrouiller('cart', oui);
    if (oui) setTimeout(() => $('[data-cd-close]', d)?.focus({ preventScroll: true }), 80);
    return true;
  };
  AP.openCart = ouvrirPanier;

  const idSectionPanierPage = () => {
    const m = $('[data-main-cart]');
    return m ? m.closest('.shopify-section')?.id.replace('shopify-section-', '') : null;
  };
  const sectionsDemandees = () => ['ap-cart-drawer', idSectionPanierPage()].filter(Boolean).join(',');

  const majCompteurs = (n) => {
    $$('[data-cart-count]').forEach((c) => {
      const avant = c.dataset.count;
      c.textContent = n;
      c.dataset.count = n;
      if (String(avant) !== String(n)) { c.classList.remove('is-bump'); void c.offsetWidth; c.classList.add('is-bump'); }
      c.closest('a')?.setAttribute('aria-label', `Panier (${n})`);
    });
  };

  const appliquerSections = (sections) => {
    if (!sections) return;
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
    if (sections['ap-cart-drawer']) {
      const doc = parse(sections['ap-cart-drawer']);
      const neuf = $('[data-cd-inner]', doc);
      const actuel = $('[data-cd-inner]');
      if (neuf && actuel) actuel.innerHTML = neuf.innerHTML;
      const compte = $('[data-cd-count]', doc);
      if (compte) majCompteurs(compte.dataset.cdCount);
    }
    const idPage = idSectionPanierPage();
    if (idPage && sections[idPage]) {
      const doc = parse(sections[idPage]);
      const neuf = $('[data-main-cart]', doc);
      const actuel = $('[data-main-cart]');
      if (neuf && actuel) actuel.innerHTML = neuf.innerHTML;
    }
    document.dispatchEvent(new CustomEvent('ap:cart:updated'));
  };

  const erreurPanier = (msg) => {
    const e = $('[data-cd-error]');
    if (e) { e.textContent = msg; e.hidden = false; }
    AP.toast(msg);
  };

  AP.cart = {
    async add(items, { ouvrir = true } = {}) {
      const corps = { items: items.map((i) => ({ id: +i.id, quantity: +i.quantity || 1, properties: i.properties || undefined })), sections: sectionsDemandees(), sections_url: window.location.pathname };
      const r = await fetch(`${AP.routes.cartAdd}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(corps) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        erreurPanier(data.description || data.message || AP.strings.error);
        throw new Error(data.description || 'add');
      }
      appliquerSections(data.sections);
      if (ouvrir && !ouvrirPanier(true)) AP.toast(AP.strings.added);
      return data;
    },
    async change(ligne, quantite) {
      const r = await fetch(`${AP.routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line: +ligne, quantity: +quantite, sections: sectionsDemandees(), sections_url: window.location.pathname }) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { erreurPanier(data.description || data.message || AP.strings.error); return; }
      appliquerSections(data.sections);
      majCompteurs(data.item_count);
    },
  };

  /* Ouvrir / fermer */
  document.addEventListener('click', (e) => {
    const lien = e.target.closest('[data-cart-open]');
    if (lien && drawer() && !document.body.classList.contains('ap-t-cart')) { e.preventDefault(); ouvrirPanier(true); return; }
    if (e.target.closest('[data-cd-close]') || e.target.matches('[data-cd-overlay]')) ouvrirPanier(false);
  });
  document.addEventListener('keydown', (e) => {
    const d = drawer();
    if (!d || !d.classList.contains('is-open')) return;
    if (e.key === 'Escape') ouvrirPanier(false);
    pieger(d, e);
  });

  /* Quantités */
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-qty-change]');
    if (!b) return;
    const ligne = b.closest('[data-line]');
    if (!ligne) return;
    e.preventDefault();
    ligne.style.opacity = '.45';
    ligne.style.pointerEvents = 'none';
    await AP.cart.change(ligne.dataset.line, b.dataset.qtyChange);
  });

  /* Ajout rapide (cartes, suggestions) */
  const fermerPops = (sauf) => $$('[data-qa-pop].is-open').forEach((p) => { if (p !== sauf) { p.classList.remove('is-open'); p.previousElementSibling?.setAttribute('aria-expanded', 'false'); } });
  document.addEventListener('click', async (e) => {
    const toggle = e.target.closest('[data-qa-toggle]');
    if (toggle) {
      e.preventDefault();
      const pop = toggle.nextElementSibling;
      const oui = !pop.classList.contains('is-open');
      fermerPops(pop);
      pop.classList.toggle('is-open', oui);
      toggle.setAttribute('aria-expanded', String(oui));
      if (oui) pop.querySelector('button:not([disabled])')?.focus({ preventScroll: true });
      return;
    }
    const add = e.target.closest('[data-qa-add]');
    if (add) {
      e.preventDefault();
      const cible = add.closest('[data-qa]')?.querySelector('.ap-qa') || add;
      cible.classList.add('is-loading');
      add.disabled = true;
      try {
        await AP.cart.add([{ id: add.dataset.qaAdd, quantity: 1 }]);
        cible.classList.add('is-done');
        setTimeout(() => cible.classList.remove('is-done'), 1600);
      } catch (_) { /* message déjà affiché */ }
      cible.classList.remove('is-loading');
      add.disabled = false;
      fermerPops();
      return;
    }
    if (!e.target.closest('[data-qa-pop]')) fermerPops();
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') fermerPops(); });

  /* Formulaires produit classiques → AJAX */
  document.addEventListener('submit', async (e) => {
    const f = e.target.closest('form[data-ap-product-form]');
    if (!f) return;
    e.preventDefault();
    const btn = f.querySelector('[type="submit"]');
    btn?.classList.add('is-loading');
    const fd = new FormData(f);
    const props = {};
    for (const [k, v] of fd.entries()) { const m = /^properties\[(.+)\]$/.exec(k); if (m && v) props[m[1]] = v; }
    try {
      await AP.cart.add([{ id: fd.get('id'), quantity: fd.get('quantity') || 1, properties: Object.keys(props).length ? props : undefined }]);
    } catch (_) { /* affiché */ }
    btn?.classList.remove('is-loading');
  });

  /* ==========================================================================
     Carrousels + onglets
     ========================================================================== */
  const rails = () => {
    $$('[data-rail]').forEach((rail) => {
      const section = rail.closest('.ap-section') || rail.parentElement;
      const prev = $('[data-rail-prev]', section);
      const next = $('[data-rail-next]', section);
      const piste = () => $('.ap-rail__panel:not([hidden]) [data-rail-track]', rail);
      const etat = () => {
        const p = piste();
        if (!p || !prev) return;
        prev.disabled = p.scrollLeft < 8;
        next.disabled = p.scrollLeft + p.clientWidth > p.scrollWidth - 8;
      };
      const pas = (sens) => {
        const p = piste();
        const carte = p?.firstElementChild;
        if (!carte) return;
        const largeur = carte.getBoundingClientRect().width + parseFloat(getComputedStyle(p).columnGap || 16);
        p.scrollBy({ left: sens * largeur * (window.innerWidth > 1100 ? 2 : 1), behavior: reduit ? 'auto' : 'smooth' });
      };
      prev?.addEventListener('click', () => pas(-1));
      next?.addEventListener('click', () => pas(1));
      $$('[data-rail-track]', rail).forEach((p) => p.addEventListener('scroll', etat, { passive: true }));
      $$('[data-tab]', section).forEach((tab) => tab.addEventListener('click', () => {
        $$('[data-tab]', section).forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
        $$('[data-panel]', rail).forEach((p) => { p.hidden = p.dataset.panel !== tab.dataset.tab; });
        const p = piste();
        if (p) { p.scrollLeft = 0; $$('[data-reveal]', p).forEach((c) => c.classList.add('is-in')); }
        etat();
      }));
      etat();
    });
  };

  /* Offre : bascule 50 / 100 mL */
  const offre = () => {
    $$('[data-offer-stage]').forEach((s) => {
      $$('[data-offer-size]', s).forEach((b) => b.addEventListener('click', () => {
        const g = b.dataset.offerSize === 'g';
        $$('[data-offer-size]', s).forEach((x) => x.setAttribute('aria-selected', String(x === b)));
        $('[data-offer-price-p]', s).hidden = g;
        $('[data-offer-price-g]', s).hidden = !g;
        $('[data-offer-save-p]', s).hidden = g;
        $('[data-offer-save-g]', s).hidden = !g;
      }));
    });
  };


  /* ==========================================================================
     Phrase qui s’allume mot à mot au défilement
     ========================================================================== */
  const mots = () => {
    const blocs = $$('[data-words]:not([data-words-ok])');
    if (!blocs.length) return;
    blocs.forEach((b) => {
      b.setAttribute('data-words-ok', '');
      const texte = b.textContent.trim().split(/\s+/);
      b.setAttribute('aria-label', b.textContent.trim());
      b.innerHTML = texte.map((m) => `<span class="w" aria-hidden="true">${m.replace(/[&<>]/g, (x) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[x]))}</span>`).join(' ');
    });
    if (reduit) { $$('[data-words] .w').forEach((w) => w.classList.add('is-lit')); return; }
    let prevu = false;
    const maj = () => {
      prevu = false;
      const vh = window.innerHeight;
      $$('[data-words]').forEach((b) => {
        const r = b.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        const p = Math.min(1, Math.max(0, (vh * 0.82 - r.top) / (r.height + vh * 0.3)));
        const w = $$('.w', b);
        const n = Math.round(p * w.length);
        w.forEach((x, i) => x.classList.toggle('is-lit', i < n));
      });
    };
    window.addEventListener('scroll', () => { if (!prevu) { prevu = true; requestAnimationFrame(maj); } }, { passive: true });
    maj();
  };

  /* ==========================================================================
     Coffrets prêts : format 50 / 100 mL + ajout des 4 parfums d’un coup
     ========================================================================== */
  const coffretsPrets = () => {
    $$('[data-sets]:not([data-sets-ok])').forEach((z) => {
      z.setAttribute('data-sets-ok', '');
      let taille = 'p';
      $$('[data-set-size]', z).forEach((b) => b.addEventListener('click', () => {
        taille = b.dataset.setSize;
        $$('[data-set-size]', z).forEach((x) => { const on = x === b; x.classList.toggle('is-on', on); x.setAttribute('aria-pressed', String(on)); });
        $$('[data-set-price]', z).forEach((pr) => {
          const avant = pr.dataset[`before${taille.toUpperCase()}`];
          const apres = pr.dataset[`after${taille.toUpperCase()}`];
          const s = $('[data-before]', pr);
          if (s) s.textContent = avant;
          $('[data-after]', pr).textContent = apres;
        });
      }));
      $$('[data-set-add]', z).forEach((b) => b.addEventListener('click', async () => {
        const ids = (b.dataset[taille === 'g' ? 'idsG' : 'idsP'] || '').split(',').filter(Boolean);
        if (!ids.length) return;
        b.classList.add('is-loading');
        try { await AP.cart.add(ids.map((id) => ({ id, quantity: 1 }))); } catch (_) { /* affiché */ }
        b.classList.remove('is-loading');
      }));
    });
  };

  /* ==========================================================================
     En-tête sombre au-dessus des zones sombres pleine largeur (pied de page)
     ========================================================================== */
  const enteteSombre = () => {
    const h = $('[data-header]');
    const zones = $$('[data-header-dark]');
    if (!h || !zones.length) return;
    let prevu = false;
    const maj = () => {
      prevu = false;
      const y = h.getBoundingClientRect().bottom - 4;
      h.classList.toggle('is-dark', zones.some((z) => { const r = z.getBoundingClientRect(); return r.top <= y && r.bottom > y; }));
    };
    window.addEventListener('scroll', () => { if (!prevu) { prevu = true; requestAnimationFrame(maj); } }, { passive: true });
    maj();
  };

  /* ==========================================================================
     Démarrage
     ========================================================================== */
  const demarrer = () => {
    observerApparitions();
    annonces();
    entete();
    rails();
    offre();
    mots();
    coffretsPrets();
    enteteSombre();
    document.addEventListener('shopify:section:load', () => { observerApparitions(); rails(); offre(); mots(); coffretsPrets(); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer);
  else demarrer();
})();
